// Control Modal - Contains all mesh control inputs
import React, { useState } from 'react';
import { Control } from './Control';
import type { MeshControls } from '../types';
import { trackTopologyMode, trackPausePlay } from '../utils/analytics';

interface ControlModalProps {
  controls: MeshControls;
  onControlsChange: (controls: Partial<MeshControls>) => void;
  onRegenerate?: () => void;
}

// Meshtastic-inspired color palette for message types
const MESSAGE_COLORS = [
  { name: 'Default', value: '#22d3ee', description: 'Standard mesh traffic' },  // cyan-400
  { name: 'Position', value: '#10b981', description: 'GPS location updates' }, // emerald-500
  { name: 'Text', value: '#3b82f6', description: 'Chat messages' },     // blue-500
  { name: 'Telemetry', value: '#8b5cf6', description: 'Device metrics & sensors' }, // violet-500
  { name: 'Admin', value: '#f59e0b', description: 'Network configuration' },    // amber-500
  { name: 'Emergency', value: '#ef4444', description: 'Critical alerts' }, // red-500
];

// Tooltip component for info icons
function InfoTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const timeoutRef = React.useRef<number | null>(null);
  
  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShow(true);
  };
  
  const handleMouseLeave = () => {
    // Small delay before hiding to allow moving to tooltip
    timeoutRef.current = window.setTimeout(() => {
      setShow(false);
    }, 100);
  };
  
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  
  return (
    <div className="relative inline-block ml-1.5 group">
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          e.stopPropagation();
          setShow(!show);
        }}
        className="text-zinc-500 hover:text-zinc-400 transition-colors"
        aria-label="More information"
      >
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>
      
      {/* Tooltip positioned below, but allowed to overflow */}
      {show && (
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-[100] w-64 p-2.5 bg-zinc-800 border border-zinc-600 rounded-lg shadow-2xl text-xs text-zinc-200 leading-relaxed"
          style={{ pointerEvents: 'auto' }}
        >
          {text}
          {/* Arrow pointer */}
          <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-zinc-800 border-l border-t border-zinc-600 transform rotate-45"></div>
        </div>
      )}
    </div>
  );
}

export function ControlModal({ controls, onControlsChange, onRegenerate }: ControlModalProps) {
  const [showLegend, setShowLegend] = useState(false);
  const [showNetworkSettings, setShowNetworkSettings] = useState(true);
  const [showTrafficVisual, setShowTrafficVisual] = useState(true);
  
  return (
    <div className="space-y-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
        <h3 className="text-sm font-semibold text-white mb-1">Mesh Visualization</h3>
        <p className="text-xs text-zinc-400">Adjust parameters to explore the mesh network</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Pause/Play Toggle */}
          <button
            onClick={() => {
              const newPaused = !controls.paused;
              onControlsChange({ paused: newPaused });
              trackPausePlay(newPaused ? 'pause' : 'play');
            }}
            className={`px-3 py-1.5 border rounded-lg text-xs transition-colors flex items-center gap-1.5 ${
              controls.paused
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20'
                : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white'
            }`}
            aria-label={controls.paused ? 'Resume simulation' : 'Pause simulation'}
            title={controls.paused ? 'Resume (hover packets to inspect)' : 'Pause simulation'}
          >
            {controls.paused ? (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Play
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Pause
              </>
            )}
          </button>
          
          {/* Regen Button */}
          <button
            onClick={onRegenerate}
            className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-xs text-cyan-400 hover:bg-cyan-500/20 transition-colors flex items-center gap-1.5"
            aria-label="Regenerate mesh"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Regen
          </button>
        </div>
      </div>

      {/* Topology Mode Toggle */}
      <div className="mb-3 p-3 bg-zinc-900/50 border border-zinc-800 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-zinc-300 flex items-center">
            Topology Mode
            <InfoTooltip text="For educational visualization only. KNN: Simplified model connecting each node to k nearest neighbors. Radius: Simulates realistic range-based connections like actual LoRa radio, but with simplified parameters." />
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => {
              onControlsChange({ topologyMode: 'knn' });
              trackTopologyMode('knn');
            }}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              controls.topologyMode === 'knn'
                ? 'bg-cyan-500/20 text-cyan-400 border-2 border-cyan-500/50'
                : 'bg-zinc-800/50 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
            }`}
          >
            KNN (Simplified)
          </button>
          <button
            onClick={() => {
              onControlsChange({ topologyMode: 'radius' });
              trackTopologyMode('radius');
            }}
            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              controls.topologyMode === 'radius'
                ? 'bg-cyan-500/20 text-cyan-400 border-2 border-cyan-500/50'
                : 'bg-zinc-800/50 text-zinc-400 border border-zinc-700 hover:border-zinc-600'
            }`}
          >
            Radius (Realistic)
          </button>
        </div>
      </div>

      {/* Network Settings - Collapsible */}
      <div className="pt-2 border-t border-zinc-800">
        <button
          onClick={() => setShowNetworkSettings(!showNetworkSettings)}
          className="w-full flex items-center justify-between text-xs text-zinc-400 hover:text-zinc-300 transition-colors mb-3"
        >
          <span className="font-medium">Network Settings</span>
          <svg 
            className={`w-4 h-4 transition-transform ${showNetworkSettings ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {showNetworkSettings && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <Control label={
              <span className="flex items-center text-[11px] md:text-xs">
                Nodes: {controls.nodeCount}
                <InfoTooltip text="Total number of devices in the mesh network. Each node can relay messages to extend coverage." />
              </span>
            }>
              <input
                aria-label="Node count"
                type="range"
                min={24}
                max={180}
                value={controls.nodeCount}
                onChange={(e) => onControlsChange({ nodeCount: parseInt(e.target.value) })}
                className="w-full"
              />
            </Control>

            {/* Show KNN control or Range control based on mode */}
            {controls.topologyMode === 'knn' ? (
              <Control label={
                <span className="flex items-center text-[11px] md:text-xs">
                  Neighbors: k={controls.neighborK}
                  <InfoTooltip text="Maximum nearest neighbors each node connects to. In KNN mode, nodes typically connect to k neighbors, but may have more connections if other nodes choose them as neighbors. (KNN mode only)" />
                </span>
              }>
              <input
                aria-label="Neighbors"
                type="range"
                min={2}
                max={6}
                value={controls.neighborK}
                onChange={(e) => onControlsChange({ neighborK: parseInt(e.target.value) })}
                className="w-full"
              />
            </Control>
            ) : (
              <Control label={
                <span className="flex items-center text-[11px] md:text-xs">
                  Range: {controls.distanceUnit === 'mi' 
                    ? `${(controls.maxRange * 50).toFixed(1)} mi`
                    : `${(controls.maxRange * 80).toFixed(1)} km`
                  }
                  <InfoTooltip text="Simulated maximum connection distance for this visualization. Real Meshtastic range varies greatly with terrain, obstacles, antenna, and settings. Educational examples: Urban ~3-5 mi, Suburban ~7-10 mi, Rural ~15+ mi. Toggle mi/km below. (Radius mode only)" />
                </span>
              }>
                <div className="space-y-2">
                  <input
                    aria-label="Connection range"
                    type="range"
                    step={0.01}
                    min={0.08}
                    max={0.3}
                    value={controls.maxRange}
                    onChange={(e) => onControlsChange({ maxRange: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                  {/* Distance Unit Toggle */}
                  <div className="flex items-center justify-center gap-2 text-[10px] md:text-xs">
                    <button
                      onClick={() => onControlsChange({ distanceUnit: 'mi' })}
                      className={`px-2 py-1 rounded transition-all ${
                        controls.distanceUnit === 'mi'
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-zinc-500 hover:text-zinc-400'
                      }`}
                    >
                      Miles
                    </button>
                    <span className="text-zinc-600">|</span>
                    <button
                      onClick={() => onControlsChange({ distanceUnit: 'km' })}
                      className={`px-2 py-1 rounded transition-all ${
                        controls.distanceUnit === 'km'
                          ? 'bg-cyan-500/20 text-cyan-400'
                          : 'text-zinc-500 hover:text-zinc-400'
                      }`}
                    >
                      Kilometers
                    </button>
                  </div>
                </div>
              </Control>
            )}
          </div>
        )}
      </div>

      {/* Traffic & Visual Settings - Collapsible */}
      <div className="pt-2 border-t border-zinc-800">
        <button
          onClick={() => setShowTrafficVisual(!showTrafficVisual)}
          className="w-full flex items-center justify-between text-xs text-zinc-400 hover:text-zinc-300 transition-colors mb-3"
        >
          <span className="font-medium">Traffic & Visual</span>
          <svg 
            className={`w-4 h-4 transition-transform ${showTrafficVisual ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {showTrafficVisual && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <Control label={
              <span className="flex items-center text-[11px] md:text-xs">
                Traffic: ~{controls.traffic}/s
                <InfoTooltip text="Message transmission rate. Each packet has a hop_limit (3-7 hops) that decrements at every retransmission. When hop_limit reaches 0, the packet is dropped — preventing infinite loops in the mesh." />
              </span>
            }>
              <input
                aria-label="Traffic"
                type="range"
                min={1}
                max={24}
                value={controls.traffic}
                onChange={(e) => onControlsChange({ traffic: parseInt(e.target.value) })}
                className="w-full"
              />
            </Control>

            <Control label={
              <span className="flex items-center text-[11px] md:text-xs">
                Line opacity: {Math.round(controls.lineOpacity * 100)}%
                <InfoTooltip text="Visual opacity of mesh connections. Lower values reduce visual clutter in dense networks." />
              </span>
            }>
              <input
                aria-label="Line opacity"
                type="range"
                min={0.1}
                max={0.7}
                step={0.05}
                value={controls.lineOpacity}
                onChange={(e) => onControlsChange({ lineOpacity: parseFloat(e.target.value) })}
                className="w-full"
              />
            </Control>
          </div>
        )}
      </div>

      {/* Message Types & Routing Info - Collapsible */}
      <div className="pt-2 border-t border-zinc-800">
        <button
          onClick={() => setShowLegend(!showLegend)}
          className="w-full flex items-center justify-between text-xs text-zinc-400 hover:text-zinc-300 transition-colors"
        >
          <span className="font-medium">Message Types & Routing</span>
          <svg 
            className={`w-4 h-4 transition-transform ${showLegend ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {showLegend && (
          <div className="mt-3 space-y-3 pl-2">
            {/* Message Color Legend with selector buttons */}
            <div className="space-y-2">
              {MESSAGE_COLORS.map((color) => (
                <button
                  key={color.value}
                  onClick={() => onControlsChange({ pulseColor: color.value })}
                  className={`w-full flex items-start gap-2.5 text-xs p-2 rounded-lg transition-all text-left ${
                    controls.pulseColor === color.value
                      ? 'bg-zinc-800/80 border border-cyan-500/50'
                      : 'hover:bg-zinc-800/40 border border-transparent'
                  }`}
                >
                  <div 
                    className={`w-3 h-3 rounded-full mt-0.5 flex-shrink-0 ring-2 ${
                      controls.pulseColor === color.value 
                        ? 'ring-cyan-400' 
                        : 'ring-white/20'
                    }`}
                    style={{ backgroundColor: color.value }}
                  />
                  <div>
                    <div className="text-zinc-300 font-medium">{color.name}</div>
                    <div className="text-zinc-500 text-[11px]">{color.description}</div>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Topology & Routing Explanation */}
            <div className="pt-2 mt-2 border-t border-zinc-800 space-y-2">
              <div className="text-zinc-500 text-[11px] leading-relaxed">
                <strong className="text-zinc-400">Topology Modes:</strong>
                <br />
                <strong className="text-cyan-400">KNN (Simplified)</strong>: Each node connects to k nearest neighbors. 
                Used by default for clearer visualization with less visual clutter.
                <br />
                <strong className="text-emerald-400">Radius (Realistic)</strong>: Nodes connect to ALL neighbors within range. 
                Accurately simulates how real Meshtastic/LoRa radios work — no artificial neighbor limits!
                <br />
                <br />
                <strong className="text-zinc-400">Distance Scale:</strong>
                <br />
                In Radius mode, distances are shown in miles or kilometers based on typical LoRa ranges:
                <br />
                • 1.0 normalized = ~50 miles (~80 km) max range
                <br />
                • Default 0.18 = ~9 mi (~14 km) - typical urban/suburban
                <br />
                • Toggle between mi/km using the buttons below the range slider
              </div>
              <div className="text-zinc-500 text-[11px] leading-relaxed">
                <strong className="text-zinc-400">How Routing Works:</strong>
                <br />
                Each message starts with a <strong className="text-cyan-400">hop_limit</strong> (3-7 hops). 
                Every node that receives and retransmits the packet decrements the hop_limit by 1. 
                When it reaches 0, the packet is dropped — preventing network flooding and infinite loops.
              </div>
              <div className="text-zinc-500 text-[11px] leading-relaxed">
                This visualization uses <strong className="text-zinc-400">flood routing</strong> with Breadth-First Search (BFS), 
                mirroring how <a href="https://meshtastic.org" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline">Meshtastic</a>'s 
                mesh networking protocol operates.
              </div>
              <div className="bg-zinc-800/50 border border-zinc-700/50 rounded p-2 mt-2">
                <p className="text-[10px] text-zinc-400 italic leading-relaxed">
                  <strong className="text-cyan-400">Note:</strong> For visual clarity, this visualization uses simplified 
                  point-to-point routing. In real Meshtastic networks, broadcast messages (Position, Telemetry) flood to 
                  all neighbors simultaneously, while direct messages (Text, Admin) use optimized pathfinding.
                </p>
              </div>
              <a 
                href="https://meshtastic.org/docs/overview/mesh-algo/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-cyan-400 hover:text-cyan-300 transition-colors pt-1"
              >
                <span>Learn more about mesh algorithms</span>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Interactive Help Section */}
      <div className="pt-3 border-t border-zinc-800 space-y-2">
        <p className="text-[11px] md:text-xs text-zinc-400 flex items-start gap-2">
          <span className="text-cyan-400 mt-0.5">•</span>
          <span><strong className="text-zinc-300">Click</strong> to add nodes (Playground mode on desktop)</span>
        </p>
        <p className="text-[11px] md:text-xs text-zinc-400 flex items-start gap-2">
          <span className="text-cyan-400 mt-0.5">•</span>
          <span><strong className="text-zinc-300">Drag</strong> nodes to move — connections update live</span>
        </p>
        <p className="text-[11px] md:text-xs text-zinc-400 flex items-start gap-2">
          <span className="text-cyan-400 mt-0.5">•</span>
          <span><strong className="text-zinc-300">Hover</strong> over nodes to highlight connections</span>
        </p>
        <p className="text-[11px] md:text-xs text-zinc-400 flex items-start gap-2">
          <span className="text-cyan-400 mt-0.5">•</span>
          <span><strong className="text-zinc-300">Playground</strong> button hides content for full mesh view</span>
        </p>
      </div>

    </div>
  );
}
